import { useEffect, useState } from 'react'
import type { NewsItem } from '../../types/finance'
import { getTrendingNews } from '../../lib/news-api'

function timeAgo(publishedAt: string): string {
  const mins = Math.max(0, Math.round((Date.now() - new Date(publishedAt).getTime()) / 60000))
  if (mins < 60) return `${mins}m ago`
  const hoursAgo = Math.round(mins / 60)
  if (hoursAgo < 24) return `${hoursAgo}h ago`
  return `${Math.round(hoursAgo / 24)}d ago`
}

interface Props {
  /** Symbols to aggregate trending news around (typically the day's movers); null = movers still loading */
  seedSymbols: string[] | null
}

export default function TrendingNewsCard({ seedSymbols }: Props) {
  const [items, setItems] = useState<NewsItem[] | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (seedSymbols == null) return
    let cancelled = false
    setLoading(true)
    getTrendingNews(seedSymbols)
      .then((n) => {
        if (!cancelled) setItems(n)
      })
      .catch(() => {
        if (!cancelled) setItems([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [seedSymbols])

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <h2 className="text-slate-900 dark:text-white font-semibold text-sm">Trending news</h2>
        <span className="text-slate-500 text-xs">High-signal articles & posts</span>
      </div>

      {loading ? (
        <div className="p-5 animate-pulse space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-10 bg-slate-200 dark:bg-slate-700 rounded" />
          ))}
        </div>
      ) : !items || items.length === 0 ? (
        <p className="px-5 py-6 text-center text-slate-500 dark:text-slate-400 text-sm">No trending items right now.</p>
      ) : (
        <div className="divide-y divide-slate-200/50 dark:divide-slate-700/50">
          {items.map((n, i) => (
            <a
              key={`${n.title}-${i}`}
              href={n.url}
              target={n.url && n.url !== '#' ? '_blank' : undefined}
              rel="noreferrer"
              className="block px-5 py-3 hover:bg-slate-200/30 dark:hover:bg-slate-700/30 transition-colors"
            >
              <p className="text-sm text-slate-900 dark:text-white leading-snug">{n.title}</p>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-xs text-slate-500 dark:text-slate-400">
                <span className={n.type === 'tweet' ? 'text-sky-600 dark:text-sky-400 font-medium' : 'font-medium'}>
                  {n.type === 'tweet' ? (n.author ?? 'X') : n.source}
                </span>
                <span>·</span>
                <span>{timeAgo(n.publishedAt)}</span>
                {n.isHighEngagement && (
                  <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 font-medium">
                    🔥 High engagement
                  </span>
                )}
                {n.type === 'tweet' && n.retweets != null && (
                  <span>
                    {n.retweets} RT · {n.likes} likes
                  </span>
                )}
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  )
}
